import dayjs from "dayjs";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineFreeCancellation,
  MdOutlineFrontHand,
  MdOutlinePinDrop,
  MdOutlineShareLocation,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { DATE_FORMAT, SUBSCRIPTION_FORM_KEYS } from "utils/constants";
import { filterContractNumber } from "utils/helpers/validations";

const TerminationRequest = ({
  initialValues,
  setPercent,
  bindField,
  isFieldRequired,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const contractNumberPath = "project.terminationRequest.contractNumber";
  const contractNumberField = bindField(contractNumberPath, {
    label: SUBSCRIPTION.FORM.CONTRACT_NUMBER.LABEL,
    required: isFieldRequired(contractNumberPath),
    extractValue: ({ target: { value } }) => filterContractNumber(value),
    onBlur: () => setPercent(initialValues),
  });

  const recipientPath = "project.terminationRequest.recipient";
  const recipientField = bindField(recipientPath, {
    label: SUBSCRIPTION.FORM.DESTINATION.LABEL,
    required: isFieldRequired(recipientPath),
    onBlur: () => setPercent(initialValues),
  });

  const dueDatePath = "project.terminationRequest.dueDate";
  const dueDateField = bindField(dueDatePath, {
    label: SUBSCRIPTION.FORM.DUE_DATE.LABEL,
    required: isFieldRequired(dueDatePath),
    extractValue: (date) => (date?.isValid?.() ? date : null),
    onBlur: () => setPercent(initialValues),
  });

  const madeInPath = "project.terminationRequest.madeIn";
  const madeInField = bindField(madeInPath, {
    label: SUBSCRIPTION.FORM.MADE_IN.LABEL,
    required: isFieldRequired(madeInPath),
    onBlur: () => setPercent(initialValues),
  });

  const addressPath = "project.terminationRequest.address";
  const addressField = bindField(addressPath, {
    label: SUBSCRIPTION.FORM.CURRENT_COMPANY_ADDRESS.LABEL,
    required: isFieldRequired(addressPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    get(initialValues, "project.ria", false) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST}-section`}
      >
        <Form
          type="vertical"
          title={
            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.TITLE
          }
          subtitle={
            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST
              .SUBTITLE
          }
        >
          <div className="grid--1">
            <Alert
              showIcon
              type="info"
              message={SUBSCRIPTION.FORM.TERMINATION_ALERT}
            />
            <FormField
              path={contractNumberField.path}
              label={contractNumberField.label}
              required={contractNumberField.required}
              disabled={contractNumberField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.CONTRACT_NUMBER.PLACEHOLDER}
                  label={contractNumberField.label}
                  icon={<MdOutlineFrontHand size={16} />}
                  type="custom"
                  stripSpaces={true}
                  defaultValue={contractNumberField.defaultValue}
                  {...field}
                  onChange={contractNumberField.onChange}
                  onBlur={contractNumberField.onBlur}
                />
              )}
            </FormField>
            <FormField
              path={recipientField.path}
              label={recipientField.label}
              required={recipientField.required}
              disabled={recipientField.disabled}
            >
              {(field) => (
                <Input
                  optional={SUBSCRIPTION.FORM.OPTIONAL}
                  placeholder={SUBSCRIPTION.FORM.DESTINATION.PLACEHOLDER}
                  label={recipientField.label}
                  icon={<MdOutlineShareLocation size={16} />}
                  type="custom"
                  normalizeText={true}
                  defaultValue={recipientField.defaultValue}
                  {...field}
                  onChange={recipientField.onChange}
                  onBlur={recipientField.onBlur}
                />
              )}
            </FormField>
            <FormField
              path={dueDateField.path}
              label={dueDateField.label}
              required={dueDateField.required}
              disabled={dueDateField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.DUE_DATE.PLACEHOLDER}
                  type="date"
                  format={DATE_FORMAT}
                  variant="simple"
                  label={dueDateField.label}
                  icon={<MdOutlineFreeCancellation size={16} />}
                  defaultValue={
                    get(initialValues, dueDatePath)
                      ? dayjs(get(initialValues, dueDatePath))
                      : undefined
                  }
                  {...field}
                  onChange={dueDateField.onChange}
                  onBlur={dueDateField.onBlur}
                />
              )}
            </FormField>
            <FormField
              path={madeInField.path}
              label={madeInField.label}
              required={madeInField.required}
              disabled={madeInField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.MADE_IN.PLACEHOLDER}
                  label={madeInField.label}
                  icon={<MdOutlineFreeCancellation size={16} />}
                  type="custom"
                  normalizeText={true}
                  defaultValue={madeInField.defaultValue}
                  {...field}
                  onChange={madeInField.onChange}
                  onBlur={madeInField.onBlur}
                />
              )}
            </FormField>
            <FormField
              path={addressField.path}
              label={addressField.label}
              required={addressField.required}
              disabled={addressField.disabled}
            >
              {(field) => (
                <Input
                  optional={SUBSCRIPTION.FORM.OPTIONAL}
                  placeholder={
                    SUBSCRIPTION.FORM.CURRENT_COMPANY_ADDRESS.PLACEHOLDER
                  }
                  label={addressField.label}
                  icon={<MdOutlinePinDrop size={16} />}
                  type="custom"
                  normalizeText={true}
                  defaultValue={addressField.defaultValue}
                  {...field}
                  onChange={addressField.onChange}
                  onBlur={addressField.onBlur}
                />
              )}
            </FormField>
          </div>
        </Form>
      </div>
    )
  );
};

export default TerminationRequest;
